import React, { useState, useEffect, useMemo } from 'react';
import { Card, Switch, Table, Button, Divider, Radio, Empty, Select } from 'antd';
import { CheckCircleFilled, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { RadioChangeEvent } from 'antd/lib/radio';
import styled from 'styled-components';  
import { ViewershipPoolProps, ViewershipRecord, GroupedViewershipData, AllocationStrategy, AllocationKey, AllocationPeriod } from '../../../../utils/common.interface';
import useScreenSizeHook from '../../../../utils/useScreenSizeHook';

type ColumnType = {
  title: string;
  dataIndex: string;
  key: AllocationKey | 'period' | 'select' | 'total_hov';
  width?: number;
  render?: (text: any, record: any) => React.ReactNode;
};

const StyledRadioGroup = styled(Radio.Group)`
  .ant-radio-button-wrapper {
    &:hover {
      color: #1890ff;
    }
    &-checked {
      background-color: #1890ff !important;
      border-color: #1890ff !important;
      color: white !important;
      &::before {
        background-color: #1890ff !important;
      }
      &:hover {
        color: white !important;
      }
    }
  }
`;

const StyledTable = styled(Table)`
  .exact-match-perfect-month {
    background-color: #b7eb8f !important; /* Bright green - perfect match with perfect month */
  }

  .exact-match-near-month {
    background-color: #d9f7be !important; /* Light green - perfect match with adjacent month */
  }

  .exact-match-same-quarter {
    background-color: #f4ffb8 !important; /* Pale green - perfect match in same quarter */
  }

  .exact-match-different-month {
    background-color: #e8f8d9 !important; /* Very pale green - perfect match but different month */
  }

  .exact-match {
    background-color: #d9f7be !important; /* Light green - fallback for exact matches */
  }

  .high-match {
    background-color: #e6f7ff !important; /* Light blue */
  }

  .medium-match {
    background-color: #fff1b8 !important; /* Light yellow */
  }

  .low-match {
    background-color: #ffccc7 !important; /* Light red */
  }

  .ant-table-row:hover > td {
    background-color: #bae7ff !important;
  }
`;

const ViewershipPool: React.FC<ViewershipPoolProps> = ({ 
  groupedData,
  selectedRecordKey,
  selectedRecord,
  allocationStrategy, 
  allocationPeriod,
  onSave,
}) => {
  const { height } = useScreenSizeHook();
  const [localAllocationStrategy, setLocalAllocationStrategy] = useState<AllocationStrategy>(
    allocationStrategy || {
      platform: true,
      partner: true,
      channel: true,
      territory: true
    }
  );
  const [localAllocationPeriod, setLocalAllocationPeriod] = useState<AllocationPeriod>(allocationPeriod);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(null);
  const [localSelectedPoolId, setLocalSelectedPoolId] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('All');

  const validKeys: AllocationKey[] = ['platform', 'partner', 'channel', 'territory'];

  // Define generateAllocationCode first, before it's used in useMemo
  const generateAllocationCode = (strategy: AllocationStrategy, period: AllocationPeriod): string => {
    // Base: start with partner flag (d for true, p for false)
    let code = strategy.partner ? 'd' : 'p';
    
    // Add period (m for Monthly, q for Quarterly)
    code += period === 'Monthly' ? 'm' : 'q';
    
    // Add channel and territory if selected
    if (strategy.channel) code += 'c';
    if (strategy.territory) code += 't';
    
    return code;
  };

  const filteredAllocationStrategy = useMemo(() => {
    const filtered = Object.fromEntries(
      Object.entries(localAllocationStrategy).filter(([key]) => validKeys.includes(key as AllocationKey))
    ) as Record<AllocationKey, boolean>;
    
    
    return filtered;
  }, [localAllocationStrategy, allocationPeriod]);

  
  const getMatchScore = (record: any, selectedRecord: any, strategy: AllocationStrategy) => {
    let score = 0;
    let monthScore = 0;
    let totalFields = 0;
  
    // Assign weights to each field
    const weights = {
      platform: 4,
      partner: 3,
      channel: 2,
      territory: 1,
      // Month match has its own separate score
    };
  
    const normalizeName = (s: string) => s.trim().toLowerCase().replace(/[\s._-]+/g, '');

    if (strategy.platform && selectedRecord) {
      totalFields += weights.platform;
      if (normalizeName(record.platform) === normalizeName(selectedRecord.platform)) {
        score += weights.platform;
      }
    }
    if (strategy.partner && selectedRecord) {
      totalFields += weights.partner;
      const selectedPartner = normalizeName(selectedRecord.viewershipPartner || selectedRecord.partner);
      if (normalizeName(record.partner) === selectedPartner) {
        score += weights.partner;
      }
    }
    if (strategy.channel && selectedRecord) {
      totalFields += weights.channel;
      if (record.channel.trim().toLowerCase() === selectedRecord.channel.trim().toLowerCase()) {
        score += weights.channel;
      }
    }
    if (strategy.territory && selectedRecord) {
      totalFields += weights.territory;
      if (record.territory.trim().toLowerCase() === selectedRecord.territory.trim().toLowerCase()) {
        score += weights.territory;
      }
    }
    
    // Calculate month match score (0-1 range for temporal proximity)
    if (selectedRecord && selectedRecord.month && record.period) {
      let selectedMonth = 0;
      
      // Extract month from different formats
      if (typeof selectedRecord.month === 'number') {
        selectedMonth = selectedRecord.month;
      } else if (typeof selectedRecord.month === 'string') {
        // Try to parse from formats like "2023-05" or just "5"
        if (selectedRecord.month.includes('-')) {
          selectedMonth = parseInt(selectedRecord.month.split('-')[1]);
        } else {
          selectedMonth = parseInt(selectedRecord.month);
        }
      }
      
      // Extract record month from period
      let recordMonth = 0;
      if (record.period.includes('-')) {
        recordMonth = parseInt(record.period.split('-')[1]);
      } else if (record.period.startsWith('Q')) {
        // For quarterly data, use middle month of quarter
        const quarter = parseInt(record.period.charAt(1));
        recordMonth = (quarter * 3) - 1; // Q1=2 (Feb), Q2=5 (May), Q3=8 (Aug), Q4=11 (Nov)
      }
      
      if (selectedMonth > 0 && recordMonth > 0) {
        // Calculate month difference (0-11 months max)
        const monthDiff = Math.abs(selectedMonth - recordMonth);
        
        // Convert to 0-1 score (0 = perfect match, 1 = furthest away)
        if (monthDiff === 0) {
          monthScore = 1.0; // Perfect month match
        } else if (monthDiff <= 1) {
          monthScore = 0.8; // Adjacent month
        } else if (monthDiff <= 3) {
          monthScore = 0.6; // Same quarter
        } else if (monthDiff <= 6) {
          monthScore = 0.4; // Within 6 months
        } else {
          monthScore = 0.2; // Different half of year
        }
      }
    }
  
    return { score, totalFields, monthScore };
  };
  

  const getRowClassName = (record: any) => {
    if (!selectedRecord) return '';
  
    const { score, totalFields, monthScore } = getMatchScore(record, selectedRecord, filteredAllocationStrategy);
  
    // Calculate the maximum possible score based on the strategy
    const maxPossibleScore = 
      (filteredAllocationStrategy.platform ? 4 : 0) +
      (filteredAllocationStrategy.partner ? 3 : 0) +
      (filteredAllocationStrategy.channel ? 2 : 0) +
      (filteredAllocationStrategy.territory ? 1 : 0);
  
    // Define base match categories based on field matches
    let baseClass = '';
    if (score === maxPossibleScore) {
      baseClass = 'exact-match';  // Perfect attribute match
    } else if (score >= maxPossibleScore - 1 && score >= maxPossibleScore * 0.75) {
      baseClass = 'high-match';   // High match
    } else if (score >= maxPossibleScore * 0.5) {
      baseClass = 'medium-match'; // Medium match
    } else {
      baseClass = 'low-match';    // Low match
    }
    
    // Add month-specific shading for perfect attribute matches
    if (baseClass === 'exact-match') {
      // Apply month-based shading
      if (monthScore > 0.9) {
        return 'exact-match-perfect-month'; // Perfect match with perfect month
      } else if (monthScore > 0.7) {
        return 'exact-match-near-month';    // Perfect match with adjacent month
      } else if (monthScore > 0.5) {
        return 'exact-match-same-quarter';  // Perfect match in same quarter
      } else {
        return 'exact-match-different-month'; // Perfect match but different month
      }
    }
    
    return baseClass;
  };
  
  const aggregateData = (data: GroupedViewershipData, strategy: AllocationStrategy, period: AllocationPeriod): any[] => {
    const allocationCode = generateAllocationCode(strategy, period);
    
    const aggregatedMap: Map<string, any> = new Map();
  
    Object.entries(data).forEach(([key, item]) => {
      const groupKey = validKeys
        .filter(k => strategy[k])
        .map(k => {
          switch(k) {
            case 'platform': return item.platform;
            case 'partner': return item.partner;
            case 'channel': return item.channel;
            case 'territory': return item.territory;
            default: return '';
          }
        })
        .join('|');
  
      if (period === 'Monthly') {
        Object.entries(item.time_periods).forEach(([timePeriod, hours]) => {
          const [year, month] = timePeriod.split('-');
          const fullGroupKey = `${groupKey}|${year}-${month}`;
          
          if (!aggregatedMap.has(fullGroupKey)) {
            aggregatedMap.set(fullGroupKey, {
              groupKey: fullGroupKey,
              platform: strategy.platform ? item.platform : 'All',
              partner: strategy.partner ? item.partner : 'All',
              channel: strategy.channel ? item.channel : 'All',
              territory: strategy.territory ? item.territory : 'All',
              period: `${year}-${month}`,
              total_hov: 0
            });
          }
          
          aggregatedMap.get(fullGroupKey).total_hov += hours;
        });
      } else { // Quarterly
        const quarterlyData: { [quarter: string]: number } = {};
        Object.entries(item.time_periods).forEach(([timePeriod, hours]) => {
          const [year, month] = timePeriod.split('-');
          const quarter = Math.floor((parseInt(month) - 1) / 3) + 1;
          const quarterKey = `${year}-Q${quarter}`;
          quarterlyData[quarterKey] = (quarterlyData[quarterKey] || 0) + hours;
        });
  
        Object.entries(quarterlyData).forEach(([quarterPeriod, hours]) => {
          const fullGroupKey = `${groupKey}|${quarterPeriod}`;
          
          if (!aggregatedMap.has(fullGroupKey)) {
            aggregatedMap.set(fullGroupKey, {
              groupKey: fullGroupKey,
              platform: strategy.platform ? item.platform : 'All',
              partner: strategy.partner ? item.partner : 'All',
              channel: strategy.channel ? item.channel : 'All',
              territory: strategy.territory ? item.territory : 'All',
              period: quarterPeriod,
              total_hov: 0
            });
          }
          
          aggregatedMap.get(fullGroupKey).total_hov += hours;
        });
      }
    });
  
    return Array.from(aggregatedMap.values());
  };
  useEffect(() => {
    // Reset selected pool when the selected record changes
    setSelectedPoolId(null);
    setLocalSelectedPoolId(null);
    
    if (allocationStrategy) {
      setLocalAllocationStrategy(allocationStrategy);
      if (allocationStrategy && allocationPeriod) {
        generateAllocationCode(allocationStrategy, allocationPeriod);
      }
    }
    setLocalAllocationPeriod(allocationPeriod);
    setIsEditing(false);
    
    // If selectedRecord has a linkedViewershipPool, set it as the selectedPoolId
    if (selectedRecord && selectedRecord.linkedViewershipPool) {
      setSelectedPoolId(selectedRecord.linkedViewershipPool);
    }
  }, [allocationStrategy, allocationPeriod, selectedRecordKey, selectedRecord]);

  const handleToggleChange = (field: AllocationKey) => (checked: boolean) => {
    const updatedStrategy = {
      ...localAllocationStrategy,
      [field]: checked,
    };
    
    setLocalAllocationStrategy(updatedStrategy);
    
    // If in edit mode, don't save immediately - wait for Save button
    if (!isEditing) {
      const allocationCode = generateAllocationCode(updatedStrategy, localAllocationPeriod);
      onSave(updatedStrategy, localAllocationPeriod, localSelectedPoolId || selectedPoolId || '');
    }
  };


  const sortedAndFilteredData = useMemo(() => {
    if (!groupedData || !selectedRecord) return [];
  
    const aggregatedData = aggregateData(groupedData, filteredAllocationStrategy, localAllocationPeriod);
  
    const filteredData = selectedPeriod === 'All' 
      ? aggregatedData 
      : aggregatedData.filter(item => item.period === selectedPeriod);
  
    const sortedData = filteredData.sort((a, b) => {
      const scoreA = getMatchScore(a, selectedRecord, filteredAllocationStrategy);
      const scoreB = getMatchScore(b, selectedRecord, filteredAllocationStrategy);
  
      // First sort by field match score
      if (scoreB.score !== scoreA.score) {
        return scoreB.score - scoreA.score;
      }
      
      // If field scores are equal, use month match as secondary sort
      if (scoreB.monthScore !== scoreA.monthScore) {
        return scoreB.monthScore - scoreA.monthScore;
      }
  
      // If everything is still equal, sort by period
      if (localAllocationPeriod === 'Monthly') {
        return parseInt(a.period) - parseInt(b.period);
      } else {
        return a.period.localeCompare(b.period);
      }
    });
  
  
    return sortedData;
  }, [groupedData, selectedRecord, filteredAllocationStrategy, localAllocationPeriod, selectedPeriod]);
  
  
  const handlePeriodChange = (e: RadioChangeEvent) => {
    const newPeriod = e.target.value as AllocationPeriod;
    setLocalAllocationPeriod(newPeriod);
    
    // If not in edit mode, save immediately
    if (!isEditing) {
      onSave(localAllocationStrategy, newPeriod, localSelectedPoolId || selectedPoolId || '');
    }
  };

  const periodOptions = useMemo(() => {
    if (!sortedAndFilteredData.length) return [];
    const periods = Array.from(new Set(sortedAndFilteredData.map(item => item.period))).sort();
    return ['All', ...periods];
  }, [sortedAndFilteredData]);


  const handleEditSaveClick = () => {
    if (isEditing) {
      onSave(localAllocationStrategy, localAllocationPeriod, localSelectedPoolId || '');
      setSelectedPoolId(localSelectedPoolId);  // Update the actual selectedPoolId
      setIsEditing(false);
    } else {
      setIsEditing(true);
      setLocalSelectedPoolId(selectedPoolId);  // Initialize local selection with current selection
    }
  };



  const getColumns = (): ColumnType[] => {
    const baseColumns: ColumnType[] = validKeys
      .filter(key => filteredAllocationStrategy[key])
      .map(key => ({
        title: key.charAt(0).toUpperCase() + key.slice(1),
        dataIndex: key.toLowerCase(),
        width: 120,
        key: key,
      }));
  
    const periodColumn: ColumnType = {
      title: localAllocationPeriod === 'Monthly' ? 'Month' : 'Quarter',
      dataIndex: 'period',
      key: 'period',
    };
  
    // Insert period column after partner column
    const partnerColumnIndex = baseColumns.findIndex(col => col.key === 'partner');
    if (partnerColumnIndex !== -1) {
      baseColumns.splice(partnerColumnIndex + 1, 0, periodColumn);
    } else {
      baseColumns.push(periodColumn);
    }
  

    return [
      {
        title: 'Select',
        dataIndex: 'groupKey',
        key: 'select',
        width: 60,
        render: (key: string) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {key === (isEditing ? localSelectedPoolId : selectedPoolId) ? (
              <CheckCircleFilled style={{ color: '#1890ff', fontSize: '20px' }} />
            ) : (
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#d9d9d9',
              }} />
            )}
          </div>
        ),
      },
      ...baseColumns,
      {
        title: 'Tot. HOV',
        dataIndex: 'total_hov',
        key: 'total_hov',
        render: (hours: number) => hours.toFixed(2),
      }
    ];
  };

  const handlePoolSelection = (key: string) => {
    if (isEditing) {
      setLocalSelectedPoolId(key);
    }
  };

  return (
    <Card 
       bodyStyle={{padding: "8px 16px 0 16px"}}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Viewership Pool</span>
          <Button 
            icon={isEditing ? <SaveOutlined /> : <EditOutlined />}
            onClick={handleEditSaveClick}
            disabled={!selectedRecord}
          >
            {isEditing ? 'Save' : 'Edit'}
          </Button>
        </div>
      }
    >
      {selectedRecord ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <h4>Allocate by:</h4>
            {validKeys.map(key => (
              <Switch
                key={key}
                checked={filteredAllocationStrategy[key]}
                onChange={handleToggleChange(key)}
                style={{ marginRight: 8 }}
                checkedChildren={key.charAt(0).toUpperCase() + key.slice(1)}
                unCheckedChildren={key.charAt(0).toUpperCase() + key.slice(1)}
                disabled={!isEditing}
              />
            ))}
          </div>
          <Divider style={{margin: 4}} />
          <div style={{ marginBottom: 16 }}>
            <h4>Period:</h4>
            <StyledRadioGroup onChange={handlePeriodChange} value={localAllocationPeriod} disabled={!isEditing}>
              <Radio.Button value="Monthly">Monthly</Radio.Button>
              <Radio.Button value="Quarterly">Quarterly</Radio.Button>
            </StyledRadioGroup>
          </div>
          <div>
              <h4>{localAllocationPeriod === 'Monthly' ? 'Month' : 'Quarter'} Filter:</h4>
              <Select
                style={{ width: 120 }}
                value={selectedPeriod}
                onChange={(value) => setSelectedPeriod(value)}
              >
                {periodOptions.map(period => (
                  <Select.Option key={period} value={period}>{period}</Select.Option>
                ))}
              </Select>
            </div>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            {sortedAndFilteredData.length > 0 ? (
              <StyledTable
                dataSource={sortedAndFilteredData.map(item => ({
                  ...item,
                  tableKey: item.groupKey 
                }))}
                rowKey="tableKey"  
                columns={getColumns()}
                pagination={false}
                onRow={(record) => ({
                  onClick: () => isEditing && handlePoolSelection(record.groupKey),
                  className: getRowClassName(record),
                  style: { cursor: isEditing ? 'pointer' : 'default' }
                })}    
                rowClassName={(record) => getRowClassName(record)}
                scroll={{ x: 'max-content', y: height - 585 }}
              />
            ) : (
              <Empty description="No matching data found for the selected record" />
            )}
          </div>
        </>
      ) : (
        <Empty 
          description={
            <span>
              Please select a record from the Allocation Details table to view the Viewership Pool
            </span>
          }
        />
      )}
    </Card>
  );
};

export default ViewershipPool;